import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, Ship, FlaskConical, FileCheck, Globe2 } from "lucide-react";
import { IMAGES } from "../data/site";
import { MaskedLine, Reveal, Eyebrow } from "../components/Reveal";
import { Marquee } from "../components/Marquee";
import { Counter } from "../components/Counter";

const PILLARS = [
  {
    n: "01", icon: FlaskConical, title: "Laboratory-verified quality",
    text: "Every cargo is tested against ASTM / IP methods before loading. Certificates of analysis travel with the documents; SGS or Bureau Veritas witnessing is arranged on request.",
  },
  {
    n: "02", icon: Ship, title: "Freight that holds its date",
    text: "Flexi-tanks, ISO tanks, drums and bulk parcels ex-Jebel Ali with carrier contracts that survived the worst freight squeezes of the last decade. Documentation — CoO, MSDS, BL — handled in-house.",
  },
  {
    n: "03", icon: FileCheck, title: "Trade compliance, built in",
    text: "REACH-conscious low-PCA grades for EU-bound rubber goods, GHS-aligned SDS files, and full export documentation for LC and CAD payment structures.",
  },
  {
    n: "04", icon: Globe2, title: "Regional cover, global reach",
    text: "Active supply lanes across the GCC, East & West Africa, South Asia and Southeast Asia — with consignment and buffer-stock arrangements for frame-contract buyers.",
  },
];

export default function WhyUs() {
  const { scrollY } = useScroll();
  const yBg = useTransform(scrollY, [0, 500], [0, 140]);

  return (
    <>
      <section data-testid="why-us-hero" className="relative isolate flex min-h-[70vh] items-end overflow-hidden noise-surface">
        <motion.div style={{ y: yBg }} className="absolute inset-0 -z-10">
          <img src={IMAGES.vessel} alt="Cargo vessel at sea" className="h-[115%] w-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#0A101D] via-[#0A101D]/70 to-[#0A101D]/40" />
        </motion.div>
        <div className="mx-auto w-full max-w-7xl px-5 pb-16 pt-40 lg:px-8">
          <Eyebrow>Why Terra Bloom</Eyebrow>
          <h1 className="mt-5 font-display text-5xl font-bold uppercase leading-[0.98] text-white sm:text-7xl">
            <MaskedLine delay={0.15}>The supplier your</MaskedLine>
            <MaskedLine delay={0.3} innerClassName="text-amber-500">QC team would choose.</MaskedLine>
          </h1>
        </div>
      </section>

      <Marquee items={["ASTM Tested", "SGS Witnessed", "ISO 9001 Aligned", "REACH Conscious", "On-Time Freight"]} />

      <section data-testid="why-us-pillars" className="bg-white py-24 md:py-32">
        <div className="mx-auto max-w-7xl px-5 lg:px-8">
          <Reveal><Eyebrow>Four pillars</Eyebrow></Reveal>
          <Reveal delay={0.1}>
            <h2 className="mt-4 max-w-2xl font-display text-4xl font-bold uppercase leading-tight text-[#0E1526] sm:text-5xl">
              Differentiators that survive due diligence
            </h2>
          </Reveal>
          <div className="mt-14 grid gap-px border border-black/10 bg-black/[0.08] md:grid-cols-2">
            {PILLARS.map((p, i) => (
              <Reveal key={p.n} delay={(i % 2) * 0.1} className="group bg-white p-10 transition-colors hover:bg-[#F1F5F9]">
                <div className="flex items-center justify-between">
                  <p.icon className="h-7 w-7 text-amber-500" />
                  <span className="font-mono text-sm text-slate-600 group-hover:text-amber-500 transition-colors">{p.n}.</span>
                </div>
                <h3 className="mt-8 font-display text-2xl font-semibold uppercase tracking-wide text-[#0E1526]">{p.title}</h3>
                <p className="mt-4 text-sm leading-relaxed text-slate-600">{p.text}</p>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section data-testid="why-us-logistics" className="relative overflow-hidden border-y border-white/10">
        <img src={IMAGES.tanker} alt="" aria-hidden className="absolute inset-0 h-full w-full object-cover opacity-25" />
        <div className="absolute inset-0 bg-[#0A101D]/75" />
        <div className="relative mx-auto grid max-w-7xl items-center gap-12 px-5 py-24 md:py-32 lg:grid-cols-2 lg:px-8">
          <div>
            <Reveal><Eyebrow>Logistics capability</Eyebrow></Reveal>
            <Reveal delay={0.1}>
              <h2 className="mt-4 font-display text-4xl font-bold uppercase leading-tight text-white sm:text-5xl">
                From refinery gate to your discharge port
              </h2>
            </Reveal>
            <Reveal delay={0.18}>
              <p className="mt-6 text-base leading-relaxed text-slate-300">
                We quote the full chain — product, packing, inspection, ocean freight and insurance — in a single
                offer. Heated bulk for slack and residue waxes, flexi-tanks for RPO and base oils, palletized cartons
                for waxes and retail lubricant packs.
              </p>
            </Reveal>
          </div>
          <Reveal delay={0.2} className="grid grid-cols-2 gap-6">
            {[
              { v: 24, s: "h", l: "Inquiry response pledge", d: 0 },
              { v: 100, s: "%", l: "Batches shipped with CoA", d: 0 },
              { v: 9, s: "", l: "Specialist product lines", d: 0 },
              { v: 24, s: "/7", l: "Desk availability", d: 0 },
            ].map((s, i) => (
              <div key={s.l} data-testid={`why-stat-${i}`} className="border border-white/15 bg-[#0A101D]/70 p-6 backdrop-blur">
                <p className="font-display text-4xl font-bold text-amber-500">
                  <Counter to={s.v} suffix={s.s} decimals={s.d} testId={`why-stat-value-${i}`} />
                </p>
                <p className="mt-2 font-mono text-[10px] uppercase tracking-[0.2em] text-slate-400">{s.l}</p>
              </div>
            ))}
          </Reveal>
        </div>
      </section>

      <section data-testid="why-us-cta" className="bg-white py-24 text-center">
        <Reveal><Eyebrow className="text-center">Put us to the test</Eyebrow></Reveal>
        <Reveal delay={0.1}>
          <h2 className="mx-auto mt-4 max-w-2xl font-display text-4xl font-bold uppercase leading-tight text-[#0E1526] sm:text-5xl">
            Send a spec sheet. Get a real answer.
          </h2>
        </Reveal>
        <Reveal delay={0.2}>
          <Link
            to="/contact"
            data-testid="why-us-quote-button"
            className="group mt-10 inline-flex items-center gap-2 bg-amber-500 px-10 py-4 text-sm font-semibold text-[#0A101D] transition-colors hover:bg-amber-400"
          >
            Request a Quote <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
          </Link>
        </Reveal>
      </section>
    </>
  );
}
